/**
 * GraphQL error code returned when a request masquerades into a company that can't be loaded —
 * deleted, renamed, purged, or simply a bad id.
 *
 * Lives here because it's a contract between two separately deployed halves: the API raises it while
 * building request context (`main-server`), and the app matches on it to offer a way out instead of
 * hanging on the loading spinner (`AuthorizeUser`). Every request in that session fails identically,
 * so this is the client's only signal that the masquerade target — not the session — is the problem.
 */
export const MASQUERADE_COMPANY_NOT_FOUND = 'MASQUERADE_COMPANY_NOT_FOUND'

function hasCode(errors: any): boolean {
  return Array.isArray(errors) && errors.some((error: any) => error?.extensions?.code === MASQUERADE_COMPANY_NOT_FOUND)
}

/**
 * True if `error` reports that the masqueraded company couldn't be loaded.
 *
 * Checks three shapes, because this failure happens while the API builds request *context* — before
 * any resolver runs — and that surfaces differently from a normal resolver error:
 *
 * - `graphQLErrors` — a resolver-level error, the shape most callers expect.
 * - `networkError.result.errors` — the one that actually fires here. A context throw makes Apollo
 *   Server answer HTTP 500, and apollo-link-http turns any non-2xx into a `ServerError` with the
 *   parsed body on `.result`, so the code never reaches `graphQLErrors` at all.
 * - `extensions.code` — a bare GraphQLError passed in directly.
 */
export function isMasqueradeCompanyNotFoundError(error: any): boolean {
  if (!error) {
    return false
  }

  if (error.extensions?.code === MASQUERADE_COMPANY_NOT_FOUND) {
    return true
  }

  return hasCode(error.graphQLErrors) || hasCode(error.networkError?.result?.errors) || hasCode(error.result?.errors)
}
